import { Injectable, Output } from '@angular/core';
import { readFiles } from '@shared/fileReaderUtils';

import { UploadStatus, ValidatorService } from '@shared/models/fileupload';
import { ISelectableData } from '@shared/models/generictablecolumn';
import { IZipCodeRangeChecker } from '@shared/rangeChecker/IZipCodeRangeChecker';

import { NumericalZipCodeRangeChecker } from '@shared/rangeChecker/NumericalZipCodeRangeChecker';
import { StringZipCodeCodeRangeChecker } from '@shared/rangeChecker/StringZipCodeCodeRangeChecker';
import { catchError, from, mergeMap, Subject, toArray } from 'rxjs';
import * as XLSX from 'xlsx';
import { ICreateZipCode, IZipCode, PalletisingService } from '../palletising.service';

function GroupBy<T>(array: T[], key: string): Record<string, T[]> {
	return array.reduce((result, currentValue) => {
		(result[currentValue[key]] = result[currentValue[key]] || []).push(currentValue);
		return result;
	}, {});
}

@Injectable({
	providedIn: 'root',
})
export class ZipCodeValidatorService implements ValidatorService {
	private statusSubject: Subject<UploadStatus> = new Subject<UploadStatus>();
	public statusSubject$ = this.statusSubject.asObservable();

	@Output() public allowedFileTypes: string[] = ['.xlsx'];

	constructor(private palletisingService: PalletisingService) {}

	validate(files: File[], _: any, context: ISelectableData<IZipCode>[]) {
		this.importFileAndCreateZipCodes(files[0], context);
	}

	createZipCodeValidators(context: ISelectableData<IZipCode>[]): { validator: IZipCodeRangeChecker; ruleNumber: number; self: string }[] {
		return context.map((zipCode) => {
			const from = Number.parseInt(zipCode.from, 10);
			const to = Number.parseInt(zipCode.to, 10);
			let validator: IZipCodeRangeChecker;
			if (!Number.isNaN(from) || !Number.isNaN(to)) {
				validator = new NumericalZipCodeRangeChecker(from, to);
			} else {
				validator = new StringZipCodeCodeRangeChecker(zipCode.from, zipCode.to);
			}

			return {
				ruleNumber: zipCode.ruleNumber,
				validator,
				self: zipCode.self,
			};
		});
	}

	importFileAndCreateZipCodes(file: File, context: ISelectableData<IZipCode>[]): void {
		const validators = this.createZipCodeValidators(context);
		const ZipCodeRules = GroupBy(validators, 'ruleNumber');

		readFiles([file], 'binary')
			.pipe(
				mergeMap((text) => {
					try {
						const templates = this.extractData(text);
						const grouped = GroupBy(templates, 'ruleNumber');

						return from(Object.entries(grouped));
					} catch (error) {
						console.error(error);
						throw new Error('Failed to parse XLSX');
					}
				}),
				mergeMap(([rule, zipCodes]) => {
					const existingZipCodes = ZipCodeRules[rule];
					if (existingZipCodes) {
						// filter out overlapping zip codes
						const OverlappingZipCodes = existingZipCodes.filter((existingZipCode) => {
							return zipCodes.some((zipCode) => {
								return existingZipCode.validator.doesRangeOverlap(zipCode.from, zipCode.to);
							});
						});
						const deleteObservable = from(OverlappingZipCodes).pipe(
							mergeMap((zipCode) =>
								this.palletisingService.deleteZipCodeBySelf(zipCode.self).pipe(
									catchError((error) => {
										console.error(error);
										throw new Error('Failed to delete rule Api Error');
									}),
								),
							),
						);

						// wait for all delete observables to complete
						// and then pass the zip codes
						return deleteObservable.pipe(
							toArray(),
							mergeMap(() => from(zipCodes)),
						);
					}
					// if there isn't a existing zipCode rule, then just pass the zip codes
					return from(zipCodes);
				}),
				mergeMap((importedMapping) => {
					const request: ICreateZipCode = {
						ruleNumber: importedMapping.ruleNumber,
						from: importedMapping.from,
						to: importedMapping.to,
						description: importedMapping.description,
					};

					return this.palletisingService.createZipCode(request).pipe(
						catchError((error) => {
							console.error(error);
							throw new Error('Failed to create rule Api Error');
						}),
					);
				}),
			)
			.subscribe({
				complete: () => {
					this.statusSubject.next({ status: 'success', data: 'Import complete' });
				},
				error: (error: Error) => {
					this.statusSubject.next({ status: 'error', data: error.message });
				},
			});
	}

	extractData(file: ArrayBuffer): ICreateZipCode[] {
		if (file.byteLength === 0) {
			throw new Error('File is empty');
		}

		const sheet = XLSX.read(file, { type: 'binary' }).Sheets['ZipCodes'];

		const rows: Array<[ruleNumber: string | number, from: string | number, to: string | number, description?: string]> = XLSX.utils.sheet_to_json(sheet, { header: 1 });

		if (rows.length === 1) {
			throw new Error('File contains only header');
		}

		// remove header
		const header = rows.shift();

		if (header[0] !== 'ruleNumber' || header[1] !== 'from' || header[2] !== 'to' || header[3] !== 'description') {
			throw new Error('Invalid headers');
		}

		const zipCodes = rows.reduce((acc, row) => {
			const [ruleNumber, from, to, description] = row;

			// skip empty rows
			if (!ruleNumber || !from || !to) {
				return acc;
			}

			acc.push({
				ruleNumber: typeof ruleNumber === 'string' ? Number.parseInt(ruleNumber) : ruleNumber,
				from: typeof from === 'number' ? from.toFixed(0) : from,
				to: typeof to === 'number' ? to.toFixed(0) : to,
				description: description && typeof description === 'string' ? description.trim() : description,
			});

			return acc;
		}, [] as ICreateZipCode[]);

		return zipCodes;
	}
}
